/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  DocumentVersionWithoutGeneratedFieldsType,
  DocumentVersionType,
  PageTypeEnum,
  PageGuideType,
} from '../algorithm/types';

/** Just a unique temporary id for this unsaved page */
export function newGuideTempId(document_version_id: string, name: string) {
  return `___${document_version_id}___${name}`;
}

export function createNewGuide({
  document_version_id,
  name,
  order,
}: {
  document_version_id: string;
  name: string;
  order: number;
}) {
  const newPage: PageGuideType = {
    id: newGuideTempId(document_version_id, name),
    pageType: PageTypeEnum.GUIDE,
    name,
    document_version_id,
    content: '',
    isRoot: true,
    order,
  };
  return newPage;
}

/**
 *
 * This modifies the documentVersion inplace.
 *
 * Note: This takes a DocumentVersionWithoutGeneratedFieldsType but will modify documentVersion to be a DocumentVersionType,
 *       adding the generated field
 */
export function documentVersionUpdateGeneratedFields(
  documentVersion: DocumentVersionWithoutGeneratedFieldsType | DocumentVersionType
): asserts documentVersion is DocumentVersionType {
  const pages = Object.values(documentVersion.pages);
  const ver = documentVersion as DocumentVersionType;
  ver.pageIds = Object.keys(ver.pages);
  ver.nameToPageId = {};
  ver.guidePageIds = [];
  const guideNamesFound = new Set<string>();
  delete ver.algorithmRootPageId;
  pages.forEach((page) => {
    ver.nameToPageId[page.name] = page.id;
    if (page.pageType === PageTypeEnum.GUIDE) {
      ver.guidePageIds.push(page.id);
      if (guideNamesFound.has(page.name)) {
        console.error(`Document has two guides with the same name: ${page.name}`, JSON.stringify(ver));
        throw new Error(`Document has two guides with the same name: ${page.name}`);
      }
      guideNamesFound.add(page.name);
    } else if (page.isRoot) {
      if (ver.algorithmRootPageId) {
        console.warn(
          `Multiple algorithm root pages! ${ver.algorithmRootPageId} and ${page.id} in document ${ver.title}`
        );
      }
      ver.algorithmRootPageId = page.id;
    }
  });
  const getOrder = (id: string) => (ver.pages[id] as PageGuideType).order;
  ver.guidePageIds.sort((a, b) => getOrder(a) - getOrder(b));
}

export function documentVersionUpdateGeneratedFieldsList(
  documentVersions: DocumentVersionWithoutGeneratedFieldsType[]
): DocumentVersionType[] {
  documentVersions.forEach(documentVersionUpdateGeneratedFields);
  return documentVersions as DocumentVersionType[];
}

export function documentVersionUpdateGeneratedFieldsMap(
  documentVersions: Record<string, DocumentVersionWithoutGeneratedFieldsType>
): Record<string, DocumentVersionType> {
  const res: Record<string, DocumentVersionType> = {};
  for (const [key, value] of Object.entries(documentVersions)) {
    documentVersionUpdateGeneratedFields(value);
    res[key] = value as DocumentVersionType;
  }
  return res;
}

/** This returns a document version with the unneeded fields removed */
export function documentVersionRemoveGeneratedFields(
  documentVersion: DocumentVersionType
): DocumentVersionWithoutGeneratedFieldsType {
  // delete autogenerated fields
  const { algorithmRootPageId, guidePageIds, pageIds, nameToPageId, likedByYou, likesCount, ...ver2 } = documentVersion;
  // Quick sanity check that we didn't delete a field that is not autogenerated
  // eslint-disable-next-line prettier/prettier
  ver2 satisfies DocumentVersionWithoutGeneratedFieldsType;
  return ver2;
}
